'use client'

import React from 'react'
import {
  Fingerprint,
  Building2,
  BookOpen,
  User,
  CheckCircle,
  XCircle,
  ArrowDown,
  ShieldCheck,
  AlertTriangle,
} from 'lucide-react'

/**
 * Brand Disambiguation Diagrams
 * Visual components for the brand disambiguation pillar post.
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6.
 * Schematic only: no fabricated quantitative data.
 */

type FootprintLevel = 'low' | 'medium' | 'high'
const FOOTPRINT_FILLED: Record<FootprintLevel, number> = {
  low: 1,
  medium: 2,
  high: 3,
}

function FootprintBar({ level }: { level: FootprintLevel }) {
  const filled = FOOTPRINT_FILLED[level]
  return (
    <div className='flex items-center gap-1' aria-hidden='true'>
      {[1, 2, 3].map(i => (
        <span
          key={i}
          className='h-1 w-4'
          style={{ background: i <= filled ? '#008dcc' : 'var(--border)' }}
        />
      ))}
    </div>
  )
}

/**
 * How AI resolves a shared brand name: candidate entities, then the
 * default-wrong outcome versus the ground-truth-correct outcome.
 */
export function EntityResolutionDiagram() {
  const candidates = [
    {
      name: 'Your company',
      sub: 'Newer, fewer signals',
      icon: Fingerprint,
      level: 'low' as FootprintLevel,
      you: true,
    },
    {
      name: 'Larger same-named firm',
      sub: 'More coverage, bigger footprint',
      icon: Building2,
      level: 'high' as FootprintLevel,
    },
    {
      name: 'A book or product',
      sub: 'Shares the exact name',
      icon: BookOpen,
      level: 'medium' as FootprintLevel,
    },
    {
      name: 'A public figure',
      sub: 'Strong personal brand',
      icon: User,
      level: 'high' as FootprintLevel,
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          HOW AI RESOLVES YOUR BRAND NAME
        </h3>
        <p className='text-sm text-muted-foreground'>
          One name, several candidate entities, two very different outcomes
        </p>
      </div>

      <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center mb-3'>
        Candidate entities your name could resolve to
      </p>
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6'>
        {candidates.map(c => (
          <div
            key={c.name}
            className='p-3 rounded-lg bg-background border border-border/60'
            style={c.you ? { borderColor: 'rgba(0,141,204,0.45)' } : undefined}
          >
            <div className='flex items-start gap-3'>
              <div
                className='w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5'
                style={{
                  backgroundColor: c.you ? '#008dcc15' : 'var(--muted)',
                }}
              >
                <c.icon
                  className='w-4 h-4'
                  style={{
                    color: c.you ? '#008dcc' : 'var(--muted-foreground)',
                  }}
                />
              </div>
              <div className='min-w-0 flex-1'>
                <p className='text-sm font-semibold text-foreground leading-tight'>
                  {c.name}
                  {c.you && (
                    <span
                      className='ml-2 text-[10px] font-bold uppercase tracking-wider'
                      style={{ color: '#008dcc' }}
                    >
                      you
                    </span>
                  )}
                </p>
                <p className='text-xs text-muted-foreground'>{c.sub}</p>
              </div>
              <div className='flex-shrink-0 mt-1.5'>
                <FootprintBar level={c.level} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className='grid md:grid-cols-2 gap-6'>
        <div className='p-4 rounded-lg bg-background border border-border/60'>
          <div className='text-center mb-3'>
            <span
              className='inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider'
              style={{
                background: '#F90B8A15',
                color: '#F90B8A',
                border: '1px solid rgba(249,11,138,0.3)',
              }}
            >
              <XCircle className='w-3 h-3' />
              Without entity ground truth
            </span>
          </div>
          <p className='text-xs text-muted-foreground text-center mb-2'>
            AI defaults to the biggest footprint
          </p>
          <div className='flex justify-center mb-2'>
            <ArrowDown className='w-4 h-4 text-muted-foreground' />
          </div>
          <div
            className='p-3 rounded-lg'
            style={{
              background: 'rgba(249,11,138,0.05)',
              border: '1px solid rgba(249,11,138,0.3)',
            }}
          >
            <div className='flex items-center gap-2'>
              <Building2
                className='w-4 h-4 flex-shrink-0'
                style={{ color: '#F90B8A' }}
              />
              <p className='text-sm font-semibold text-foreground'>
                Larger same-named firm
              </p>
            </div>
          </div>
          <p
            className='text-xs font-semibold text-center mt-3'
            style={{ color: '#F90B8A' }}
          >
            Describes the wrong you
          </p>
        </div>

        <div className='p-4 rounded-lg bg-background border border-border/60'>
          <div className='text-center mb-3'>
            <span
              className='inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider'
              style={{
                background: 'rgba(34,197,94,0.12)',
                color: '#16a34a',
                border: '1px solid rgba(34,197,94,0.3)',
              }}
            >
              <ShieldCheck className='w-3 h-3' />
              With entity ground truth
            </span>
          </div>
          <p className='text-xs text-muted-foreground text-center mb-2'>
            AI matches your verified signals
          </p>
          <div className='flex justify-center mb-2'>
            <ArrowDown className='w-4 h-4 text-muted-foreground' />
          </div>
          <div
            className='p-3 rounded-lg'
            style={{
              background: 'rgba(34,197,94,0.06)',
              border: '1px solid rgba(34,197,94,0.3)',
            }}
          >
            <div className='flex items-center gap-2'>
              <Fingerprint
                className='w-4 h-4 flex-shrink-0'
                style={{ color: '#16a34a' }}
              />
              <p className='text-sm font-semibold text-foreground'>
                Your company
              </p>
            </div>
          </div>
          <p
            className='text-xs font-semibold text-center mt-3'
            style={{ color: '#16a34a' }}
          >
            Describes the real you
          </p>
        </div>
      </div>
    </div>
  )
}

function VerdictRow({
  label,
  value,
  ok,
}: {
  label: string
  value: string
  ok: boolean
}) {
  const color = ok ? '#16a34a' : '#F90B8A'
  const Icon = ok ? CheckCircle : XCircle
  return (
    <div className='flex items-center justify-between gap-3 p-2 rounded-lg bg-muted'>
      <span className='text-xs font-medium text-muted-foreground'>{label}</span>
      <span
        className='flex items-center gap-1.5 text-xs font-semibold text-right'
        style={{ color }}
      >
        <Icon className='w-3.5 h-3.5 flex-shrink-0' />
        {value}
      </span>
    </div>
  )
}

/**
 * Hallucination versus disambiguation failure: same wrong answer on the
 * surface, opposite causes, different fixes.
 */
export function DisambiguationVsHallucinationDiagram() {
  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          HALLUCINATION vs DISAMBIGUATION FAILURE
        </h3>
        <p className='text-sm text-muted-foreground'>
          Same wrong answer on the surface, opposite causes, different fixes
        </p>
      </div>

      <div className='grid md:grid-cols-2 gap-6'>
        <div className='p-4 rounded-lg bg-background border border-border/60'>
          <div className='text-center mb-4'>
            <span
              className='inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider'
              style={{
                background: '#8b5cf615',
                color: '#8b5cf6',
                border: '1px solid rgba(139,92,246,0.3)',
              }}
            >
              <AlertTriangle className='w-3 h-3' />
              Hallucination
            </span>
          </div>
          <div className='space-y-2'>
            <VerdictRow label='Entity linked' value='Correct, it is you' ok />
            <VerdictRow
              label='Facts reported'
              value='Invented or wrong'
              ok={false}
            />
          </div>
          <div className='mt-4 pt-3 border-t border-border/60'>
            <p className='text-xs text-muted-foreground'>
              <span className='font-semibold text-foreground'>Fix:</span>{' '}
              clearer, more authoritative content the model can draw from
            </p>
          </div>
        </div>

        <div className='p-4 rounded-lg bg-background border border-border/60'>
          <div className='text-center mb-4'>
            <span
              className='inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider'
              style={{
                background: '#F90B8A15',
                color: '#F90B8A',
                border: '1px solid rgba(249,11,138,0.3)',
              }}
            >
              <XCircle className='w-3 h-3' />
              Disambiguation failure
            </span>
          </div>
          <div className='space-y-2'>
            <VerdictRow
              label='Entity linked'
              value='Wrong, someone else'
              ok={false}
            />
            <VerdictRow
              label='Facts reported'
              value='Accurate, about them'
              ok
            />
          </div>
          <div className='mt-4 pt-3 border-t border-border/60'>
            <p className='text-xs text-muted-foreground'>
              <span className='font-semibold text-foreground'>Fix:</span>{' '}
              stronger entity signals: schema, sameAs, Wikidata, consistency
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
